import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useRepositoryStore } from '../stores/useRepositoryStore';
import { useSettingsStore } from '../stores/useSettingsStore';
import { PageHeader, LoadingSpinner } from '../components/ui';

export default function BillingPage() {
  const { authStatus, setAuthStatus } = useAuth();
  const { showToast } = useToast();

  // Use Zustand stores
  const organizationInfo = useRepositoryStore(state => state.organizationInfo);
  const subscription = useSettingsStore(state => state.subscription);
  const members = useSettingsStore(state => state.members);
  const invites = useSettingsStore(state => state.invites);
  const loadingSubscription = useSettingsStore(state => state.loadingSubscription);
  const currentUserRole = useSettingsStore(state => state.currentUserRole);
  const hasLoadedOnce = useSettingsStore(state => state.hasLoadedOnce);
  const setCurrentUsername = useSettingsStore(state => state.setCurrentUsername);
  const loadSubscriptionData = useSettingsStore(state => state.loadSubscriptionData);
  const removeMemberFromStore = useSettingsStore(state => state.removeMember);
  const cancelInviteFromStore = useSettingsStore(state => state.cancelInvite);
  const inviteMemberToStore = useSettingsStore(state => state.inviteMember);
  const checkSubscriptionStatusInStore = useSettingsStore(state => state.checkSubscriptionStatus);

  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');

  // Set username in store and load data only once
  useEffect(() => {
    if (authStatus.user?.login) {
      setCurrentUsername(authStatus.user.login);
    }
  }, [authStatus.user?.login, setCurrentUsername]);

  useEffect(() => {
    if (!hasLoadedOnce && authStatus.user?.login && authStatus.currentOrganization) {
      loadSubscriptionData();
    }
  }, [hasLoadedOnce, authStatus.user?.login, authStatus.currentOrganization]);

  async function handleUpgrade() {
    try {
      const response = await fetch('/api/organizations/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ billingInterval }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (error) {
      showToast('Failed to start checkout', 'error');
    }
  }

  async function handleCheckStatus() {
    const result = await checkSubscriptionStatusInStore();

    if (result.success && result.tier === 'PRO') {
      showToast('Subscription activated! You now have Pro access!', 'success');
      setTimeout(() => window.location.reload(), 1000);
    } else if (result.error) {
      if (result.error === 'Subscription still processing') {
        showToast(
          'Subscription still processing. The webhook may take a moment. Try again in a few seconds.',
          'info'
        );
      } else {
        showToast('Failed to check subscription status', 'error');
      }
    }
  }

  async function handleManageSubscription() {
    try {
      const response = await fetch('/api/organizations/subscription/portal', {
        method: 'POST',
        credentials: 'same-origin',
      });
      const data = await response.json();
      if (data.portalUrl) {
        window.open(data.portalUrl, '_blank');
      }
    } catch (error) {
      showToast('Failed to open billing portal', 'error');
    }
  }

  async function removeMember(member: { githubUserId: number; githubUsername: string }) {
    if (!confirm(`Remove ${member.githubUsername} from the organization?`)) {
      return;
    }

    try {
      await removeMemberFromStore(member.githubUserId);
      showToast('Member removed', 'success');
    } catch (error) {
      showToast('Failed to remove member', 'error');
    }
  }

  async function cancelInvite(invite: { id: string }) {
    if (!confirm('Cancel this invite?')) {
      return;
    }

    try {
      await cancelInviteFromStore(invite.id);
      showToast('Invite cancelled', 'success');
    } catch (error) {
      showToast('Failed to cancel invite', 'error');
    }
  }

  async function inviteMember() {
    const username = prompt('Enter GitHub username to invite:');
    if (!username) return;

    const result = await inviteMemberToStore(username);

    if (result.success) {
      showToast(`Invite sent to ${username}`, 'success');
      alert(`Share this invite link: ${result.inviteUrl}`);
    } else if (result.requiresUpgrade) {
      showToast(
        result.error || 'Member limit reached. Upgrade to Pro for unlimited members.',
        'error'
      );
      if (
        confirm(
          "You've reached the member limit for the free tier. Would you like to upgrade to Pro?"
        )
      ) {
        handleUpgrade();
      }
    } else {
      showToast(result.error || 'Failed to send invite', 'error');
    }
  }

  function formatSubscriptionStatus() {
    const subStatus = subscription?.subscriptionStatus;
    let status = '';

    if (subStatus === 'active') status = 'Active';
    else if (subStatus === 'canceled') status = 'Canceled';
    else if (subStatus === 'canceling') status = 'Canceling';
    else if (subStatus === 'past_due') status = 'Past Due';
    else status = subStatus || 'Unknown';

    const details = subscription?.subscriptionDetails;
    if (details && details.current_period_end) {
      const endDate = new Date(details.current_period_end * 1000);
      const dateStr = endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

      if (details.cancel_at_period_end) {
        return `${status} (ends ${dateStr})`;
      } else if (subStatus === 'active') {
        return `${status} (renews ${dateStr})`;
      }
    }

    return status;
  }

  const canManageMembers = currentUserRole === 'OWNER' || currentUserRole === 'ADMIN';
  const canManageBilling = currentUserRole === 'OWNER';

  return (
    <div className="page">
      <PageHeader title="Billing & Membership" subtitle="Manage subscription and team members" />

      <div className="page-content">
        {/* GitHub App Installation - Priority when no organization */}
        {!authStatus.currentInstallation && (
          <div className="settings-section">
            <h3>Get Started</h3>
            <p className="section-description">Install the GitHub App to start managing freezes</p>
            <div className="empty-state" style={{ marginTop: '20px' }}>
              <div className="empty-icon">
                <i className="fa-solid fa-download"></i>
              </div>
              <h3>GitHub App Not Installed</h3>
              <p>Install the GitHub App on your repositories to start using Yukon</p>
              <div className="empty-state-actions">
                <a
                  href={`https://github.com/apps/${import.meta.env.VITE_PUBLIC_GITHUB_APP_SLUG || 'yukon-freeze-manager'}/installations/new`}
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-brands fa-github"></i>
                  <span>Configure GitHub App</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Membership & Billing - Only show if organization exists */}
        {authStatus.currentInstallation && (
          <>
            {/* Subscription Status */}
            {loadingSubscription ? (
              <LoadingSpinner message="Loading subscription details..." />
            ) : (
              canManageBilling && (
                <div className="subscription-card">
                  <div className="subscription-header">
                    <div>
                      <h4>Current Organization</h4>
                      <p className="org-name">{authStatus.currentOrganization?.name}</p>
                    </div>
                    <div
                      className={`subscription-tier ${subscription?.tier === 'PRO' ? 'tier-pro' : 'tier-free'}`}
                    >
                      <i
                        className={`fa-solid ${subscription?.tier === 'PRO' ? 'fa-crown' : 'fa-cube'}`}
                      ></i>
                      <span>{subscription?.tier === 'PRO' ? 'Pro' : 'Free'}</span>
                    </div>
                  </div>

                  <div className="subscription-details">
                    {organizationInfo && (
                      <div className="detail-item">
                        <span className="detail-label">Repository Limit</span>
                        <span
                          className={`detail-value ${organizationInfo.configuredRepos >= organizationInfo.repoLimit && subscription?.tier !== 'PRO' ? 'text-warning' : ''}`}
                        >
                          {subscription?.tier === 'PRO'
                            ? 'Unlimited'
                            : `${organizationInfo.configuredRepos || 0} of ${organizationInfo.repoLimit || 3}`}
                        </span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Member Limit</span>
                      <span
                        className={`detail-value ${members.length + invites.length >= (subscription?.memberLimit || 3) && subscription?.tier !== 'PRO' ? 'text-warning' : ''}`}
                      >
                        {subscription?.tier === 'PRO'
                          ? 'Unlimited'
                          : `${members.length + invites.length} of ${subscription?.memberLimit || 3}`}
                      </span>
                    </div>
                    {organizationInfo &&
                      organizationInfo.availableRepos > organizationInfo.configuredRepos && (
                        <div className="detail-item">
                          <span className="detail-label">Available to Add</span>
                          <span className="detail-value">
                            {organizationInfo.availableRepos - organizationInfo.configuredRepos}{' '}
                            more repositories
                          </span>
                        </div>
                      )}
                    {subscription?.subscriptionStatus && (
                      <div className="detail-item">
                        <span className="detail-label">Status</span>
                        <span
                          className={`detail-value ${subscription.subscriptionStatus === 'canceled' || subscription.subscriptionStatus === 'canceling' ? 'text-warning' : ''} ${subscription.subscriptionStatus === 'past_due' ? 'text-danger' : ''}`}
                        >
                          {formatSubscriptionStatus()}
                        </span>
                      </div>
                    )}
                    {subscription?.upcomingInvoice &&
                      !subscription.subscriptionDetails?.cancel_at_period_end && (
                        <div className="detail-item">
                          <span className="detail-label">Next Payment</span>
                          <span className="detail-value">
                            {new Date(
                              subscription.upcomingInvoice.period_end * 1000
                            ).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </span>
                        </div>
                      )}
                  </div>

                  <div className="subscription-actions">
                    {subscription?.tier === 'FREE' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div className="billing-interval-toggle">
                          <button
                            className={`interval-btn ${billingInterval === 'monthly' ? 'active' : ''}`}
                            onClick={() => setBillingInterval('monthly')}
                          >
                            <div className="interval-label">Monthly</div>
                            <div className="interval-price">$15/month</div>
                          </button>
                          <button
                            className={`interval-btn ${billingInterval === 'yearly' ? 'active' : ''}`}
                            onClick={() => setBillingInterval('yearly')}
                          >
                            <div className="interval-label">
                              Yearly
                              <span className="save-badge">2 Months FREE</span>
                            </div>
                            <div className="interval-price">$150/year</div>
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button onClick={handleUpgrade} className="btn btn-primary">
                            <i className="fa-solid fa-crown"></i>
                            <span>
                              Upgrade to Pro -{' '}
                              {billingInterval === 'yearly' ? '$150/year' : '$15/month'}
                            </span>
                          </button>
                          <button onClick={handleCheckStatus} className="btn btn-secondary">
                            <i className="fa-solid fa-rotate"></i>
                            <span>Check Status</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={handleManageSubscription} className="btn btn-secondary">
                        <i className="fa-solid fa-credit-card"></i>
                        <span>Manage Subscription</span>
                      </button>
                    )}
                  </div>

                  {subscription?.tier === 'FREE' && (
                    <div className="pro-benefits">
                      <h5>Pro Benefits:</h5>
                      <ul>
                        <li>
                          <i className="fa-solid fa-check"></i> Unlimited repositories
                        </li>
                        <li>
                          <i className="fa-solid fa-check"></i> Unlimited team members
                        </li>
                        <li>
                          <i className="fa-solid fa-check"></i> Priority support
                        </li>
                        <li>
                          <i className="fa-solid fa-check"></i> Advanced scheduling features
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )
            )}

            {/* Team Members */}
            {loadingSubscription ? null : (
              <div className="team-section">
                <h4>Organization Members</h4>
                <div className="members-list">
                  {members.map(member => (
                    <div key={member.id} className="member-item">
                      <div className="member-info">
                        <span className="member-name">{member.githubUsername}</span>
                        <span className="member-role">{member.role}</span>
                      </div>
                      {member.role !== 'OWNER' && canManageMembers && (
                        <button
                          className="btn-icon delete-btn"
                          onClick={() => removeMember(member)}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pending Invites */}
                {invites.length > 0 && canManageMembers && (
                  <div style={{ marginTop: '20px' }}>
                    <h5 style={{ marginBottom: '10px' }}>Pending Invites</h5>
                    <div className="members-list">
                      {invites.map(invite => (
                        <div key={invite.id} className="member-item">
                          <div className="member-info">
                            <span className="member-name">
                              {invite.githubUsername || invite.email}
                            </span>
                            <span className="member-role" style={{ opacity: 0.7 }}>
                              Pending
                            </span>
                          </div>
                          <button
                            className="btn-icon delete-btn"
                            onClick={() => cancelInvite(invite)}
                          >
                            <i className="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {canManageMembers && (
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: '15px' }}
                    onClick={inviteMember}
                  >
                    <i className="fa-solid fa-user-plus"></i>
                    <span>Invite Member</span>
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
